/**
 * ComposerSuggestions — quick-fill chips rendered above the InlineChat
 * composer.
 *
 * Used by Pulse context-card chats to offer answer starters for the
 * agent's question (e.g. "Mornings only, 6am to noon" for the working-
 * hours card). Clicking a chip auto-sends the suggestion as a human
 * message via `onPick`; once any human reply lands, the chip row hides
 * itself so the user is not nudged toward another canned answer.
 *
 * Behavior:
 *   - Hidden when `suggestions` is undefined / empty.
 *   - Hidden when `hasHumanReply` is true (the conversation is past the
 *     opener moment; chips would clutter rather than help).
 *   - Click → `onPick(text)`. The composer-side handler fires the same
 *     send path as the Enter key, so all the WS / persist plumbing is
 *     identical to a typed reply.
 */

interface ComposerSuggestionsProps {
  suggestions: string[] | undefined;
  hasHumanReply: boolean;
  onPick: (text: string) => void;
}

export function ComposerSuggestions({
  suggestions,
  hasHumanReply,
  onPick,
}: ComposerSuggestionsProps) {
  if (!suggestions || suggestions.length === 0) return null;
  if (hasHumanReply) return null;

  return (
    <div
      data-testid="composer-suggestions"
      className="mb-2 flex flex-wrap items-center gap-1.5"
    >
      <span
        className="text-[10px] uppercase tracking-[0.16em] mr-0.5"
        style={{
          color: "var(--text-tertiary)",
          fontFamily: "var(--font-mono)",
        }}
      >
        Try
      </span>
      {suggestions.map((s) => (
        <button
          key={s}
          type="button"
          data-testid="composer-suggestion-chip"
          className="conversation-empty-chip"
          onClick={() => onPick(s)}
        >
          {s}
        </button>
      ))}
    </div>
  );
}
